// frontend/src/pages/TransferAccept.jsx
//
// [NEW] Página pública de repasse — /repasse/:token
//
// A NICO nunca media a venda do veículo (isso acontece inteiramente fora do
// app). Essa tela só existe pra quem RECEBEU o link do vendedor (WhatsApp,
// etc.) confirmar que quer assumir o histórico daquele veículo na própria
// conta. Ver backend/routes/transfers.js pra regra completa.
//
// Fluxo:
//  1. Carrega o preview público (GET /transfers/:token) — não exige login.
//  2. Se o usuário não estiver logado, mostra o card do veículo + pede pra
//     entrar ou criar conta (mantendo o token na URL via ?redirect=).
//  3. Se estiver logado, mostra o botão "Aceitar e assumir este veículo".
//
// [ATENÇÃO] O wrapper padrão de api.js (req() em services/api.js) redireciona
// sozinho pra /login em qualquer 401 e LIMPA o token da URL nesse processo.
// Isso quebraria esse fluxo (o token de repasse se perderia). Por isso, aqui
// a checagem de "está logado?" é feita direto via localStorage.getItem
// ('nico_token') ANTES de chamar transfersAPI.accept — nunca deixamos a
// chamada em si estourar um 401 nessa tela.
//
// [ATENÇÃO 2 — resolvido em 29/ago/2026] O redirecionamento pra /login usa
// `?redirect=/repasse/:token`. LoginPage.jsx agora lê esse parâmetro e volta pra cá
// depois de autenticar — tanto no login por email/senha quanto via Google (nesse
// caso o destino atravessa o round-trip do OAuth embutido no `state`, ver
// server.js /api/auth/google e /api/auth/google/callback). Antes disso o Google
// especificamente perdia o redirect no meio do caminho e sempre mandava pra
// /garagem — foi o que causou um repasse testado não ser efetuado (a tela de
// aceitar nunca era re-exibida depois do login).
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { transfersAPI } from '../services/api';
import VehicleIllustration from '../components/VehicleIllustration';
import useIsMobile from '../hooks/useIsMobile';
import toast from 'react-hot-toast';

export default function TransferAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const m = useIsMobile();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const loggedIn = !!localStorage.getItem('nico_token');

  useEffect(() => {
    transfersAPI.preview(token)
      .then(setPreview)
      .catch(err => setError(err.message || 'Link de repasse não encontrado ou já usado'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await transfersAPI.accept(token);
      toast.success(
        res.result === 'merged'
          ? 'Histórico anterior unido ao seu veículo! 🎉'
          : 'Veículo assumido com sucesso! 🎉'
      );
      navigate(`/garagem/${res.vehicle_id}`);
    } catch (err) {
      toast.error(err.message || 'Erro ao aceitar o repasse');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f4ede0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" style={{ width: '32px', height: '32px' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f4ede0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: m ? '20px' : '32px', boxSizing: 'border-box' }}>
      <div style={{ background: '#fff', borderRadius: m ? '20px' : '24px', padding: m ? '24px' : '40px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 48px rgba(15,64,35,.10)', boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: '#14562e', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '20px', marginBottom: '14px' }}>N</div>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.8px', color: '#e97117', display: 'block' }}>Repasse de veículo</span>
        </div>

        {error ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', opacity: 0.4, marginBottom: '12px' }}>🚫</div>
            <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: '18px', color: '#14562e', margin: '0 0 8px' }}>Não foi possível abrir esse link</h2>
            <p style={{ color: '#6a7268', fontSize: '14px', lineHeight: 1.5, margin: 0 }}>{error}</p>
            <button onClick={() => navigate('/')} style={{ marginTop: '20px', padding: '12px 22px', borderRadius: '12px', background: '#14562e', color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer', border: 'none', fontFamily: "'DM Sans', sans-serif" }}>
              Ir para o NICO
            </button>
          </div>
        ) : (
          <>
            <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: m ? '20px' : '24px', letterSpacing: '-0.02em', textAlign: 'center', margin: '0 0 6px', color: '#14562e' }}>
              Você recebeu um veículo
            </h1>
            <p style={{ color: '#6a7268', fontSize: '13.5px', textAlign: 'center', lineHeight: 1.5, margin: '0 0 24px' }}>
              <b style={{ color: '#183d24' }}>{preview.from_user_name}</b> quer repassar pra você o histórico completo deste veículo no NICO.
            </p>

            <div style={{ background: '#f4ede0', border: '1px solid #e5ddc9', borderRadius: '16px', padding: m ? '16px' : '20px', display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <VehicleIllustration type={preview.vehicle.type} color="#14562e" size={34} />
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: '16px', color: '#14562e', margin: 0, wordBreak: 'break-word' }}>
                  {preview.vehicle.nickname || `${preview.vehicle.brand} ${preview.vehicle.model}`} {preview.vehicle.year}
                </h3>
                {preview.vehicle.plate && (
                  <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, background: '#111', color: '#fff', padding: '3px 10px', borderRadius: '6px', fontSize: '12px', letterSpacing: '1.5px', display: 'inline-block', marginTop: '6px' }}>
                    {preview.vehicle.plate}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#e6f0e9', border: '1px solid rgba(20,86,46,0.15)', borderRadius: '12px', padding: '14px', marginBottom: '22px' }}>
              <span style={{ fontSize: '16px', lineHeight: 1 }}>🔒</span>
              <span style={{ fontSize: '12.5px', color: '#14562e', lineHeight: 1.5 }}>
                Todo o histórico registrado até hoje continua marcado como do dono anterior e não pode ser alterado. A partir de agora, o que acontecer com o carro é registrado na sua conta.
              </span>
            </div>

            {loggedIn ? (
              <button
                onClick={handleAccept}
                disabled={accepting}
                style={{ width: '100%', padding: '14px', borderRadius: '12px', fontWeight: 700, fontSize: '14.5px', cursor: accepting ? 'default' : 'pointer', border: 'none', background: '#e97117', color: '#fff', fontFamily: "'DM Sans', sans-serif", opacity: accepting ? 0.7 : 1 }}
              >
                {accepting ? 'Confirmando…' : 'Aceitar e assumir este veículo'}
              </button>
            ) : (
              <>
                <p style={{ fontSize: '13px', color: '#6a7268', textAlign: 'center', margin: '0 0 14px' }}>
                  Entre ou crie sua conta gratuita no NICO pra aceitar o repasse.
                </p>
                <div style={{ display: 'flex', gap: '10px', flexDirection: m ? 'column' : 'row' }}>
                  <button
                    onClick={() => navigate(`/login?redirect=/repasse/${token}`)}
                    style={{ flex: 1, padding: '14px', borderRadius: '12px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', border: '1.5px solid #14562e', background: 'transparent', color: '#14562e', fontFamily: "'DM Sans', sans-serif" }}
                  >
                    Já tenho conta
                  </button>
                  <button
                    onClick={() => navigate(`/login?tab=register&redirect=/repasse/${token}`)}
                    style={{ flex: 1, padding: '14px', borderRadius: '12px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', border: 'none', background: '#14562e', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}
                  >
                    Criar conta
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
